import React, { useState, useEffect } from "react";
import { Table, Typography, Tag, Space, Input, Badge, Tooltip } from "antd";
import { Link } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import orgsQuery from "./queries/orgsQuery";
import ParseLogo from "../shared/images/parse.png";

const { Title } = Typography;
const { Search } = Input;

const MarketplaceOrgs = () => {
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchOrgs = async (page = 1, pageSize = 20, search = "") => {
    setLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const where = {};
      if (search) {
        where.OR = [
          { name: { matchesRegex: search, options: "i" } },
          { vat_code: { matchesRegex: search, options: "i" } },
          { city: { matchesRegex: search, options: "i" } },
        ];
      }

      const variables = {
        first: pageSize,
        skip: (page - 1) * pageSize,
        where: Object.keys(where).length > 0 ? where : undefined,
        order: ["createdAt_DESC"],
      };

      const response = await axios.post(
        graphqlUrl,
        {
          query: orgsQuery,
          variables,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.marketplace_orgs?.edges || [];
      const count = response.data?.marketplace_orgs?.count || 0;

      setOrgs(edges.map((edge) => edge.node));
      setPagination((prev) => ({ ...prev, current: page, pageSize, total: count }));
    } catch (error) {
      console.error("Error fetching marketplace orgs:", error);
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs(pagination.current, pagination.pageSize, searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (newPagination) => {
    fetchOrgs(newPagination.current, newPagination.pageSize, searchValue);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchOrgs(1, pagination.pageSize, value);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "objectId",
      key: "objectId",
      render: (id) => <Link to={`/funeralcloud/orgs/${id}`}>{id}</Link>,
    },
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Link to={`/funeralcloud/orgs/${record.objectId}`}>{name}</Link>
      ),
    },
    {
      title: "P.IVA",
      dataIndex: "vat_code",
      key: "vat_code",
    },
    {
      title: "Città",
      dataIndex: "city",
      key: "city",
    },
    {
      title: "Provincia",
      dataIndex: "province",
      key: "province",
    },
    {
      title: "Organizzazione Eterno",
      dataIndex: ["org_pointer", "nome"],
      key: "org",
      render: (nome, record) =>
        record.org_pointer ? (
          <Link to={`/orgs/${record.org_pointer.objectId}`}>{nome}</Link>
        ) : (
          "-"
        ),
    },
    {
      title: "Profilo Venditore",
      dataIndex: ["marketplace_seller_pointer", "name"],
      key: "seller",
      render: (name, record) =>
        record.marketplace_seller_pointer ? (
          <Link to={`/funeralcloud/sellers/${record.marketplace_seller_pointer.objectId}`}>{name}</Link>
        ) : (
          "-"
        ),
    },
    {
      title: "Stato",
      dataIndex: "is_active",
      key: "is_active",
      render: (is_active) => (
        <Tag color={is_active ? "green" : "red"}>
          {is_active ? "ATTIVO" : "DISATTIVO"}
        </Tag>
      ),
      filters: [
        { text: "Attivo", value: true },
        { text: "Disattivo", value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: "Links",
      key: "links",
      render: (_, record) => {
        const parseDashboardUrl = `${config.parseDashboard[isProd ? "prod" : "dev"]}/browser/marketplace_org?filters=[{"field":"objectId","constraint":"eq","compareTo":"${record.objectId}"}]`;
        return (
          <Tooltip title="Visualizza in Parse Dashboard" placement="top">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={parseDashboardUrl}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease',
              }}
            >
              <img
                src={ParseLogo}
                width={20}
                alt="Parse Dashboard"
                style={{ display: 'block' }}
              />
            </a>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div style={{ margin: "16px 16px" }}>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={3} style={{ margin: 0 }}>
              <Space>
                Marketplace Organizzazioni
                <Badge overflowCount={5000} count={pagination.total} />
              </Space>
            </Title>
            <Search
              placeholder="Cerca per nome, P.IVA o città"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              style={{ width: 400 }}
            />
          </div>
        </Space>
      </div>

      <Table
        dataSource={orgs}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="objectId"
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default MarketplaceOrgs;
